// Bank Update Request Admin API Service Functions
import { fetchGetApi, fetchPutApi } from '@/util/api';

// Type definitions
interface ApiResponse {
  response: Response;
  data: any;
}

interface BankUpdateRequestFilters {
  status?: string;
  employeeId?: string;
  employeeName?: string;
  siteName?: string;
  page?: number;
  limit?: number;
}

/**
 * Get all bank update requests with optional filters
 * @param {BankUpdateRequestFilters} filters - Optional filters for status, employeeId, employeeName, siteName, page, limit
 * @returns {Promise<ApiResponse>} API response with bank update requests
 */
export const getAllBankUpdateRequests = async (filters: BankUpdateRequestFilters = {}): Promise<ApiResponse> => {
  const queryParams = new URLSearchParams();
  
  if (filters.status) {
    queryParams.append('status', filters.status);
  }
  
  if (filters.employeeId) {
    queryParams.append('employeeId', filters.employeeId);
  }
  
  if (filters.employeeName) {
    queryParams.append('employeeName', filters.employeeName);
  }
  
  if (filters.siteName) {
    queryParams.append('siteName', filters.siteName);
  }
  
  if (filters.page) {
    queryParams.append('page', filters.page.toString());
  }
  
  if (filters.limit) {
    queryParams.append('limit', filters.limit.toString());
  }
  
  const endpoint = `api/admin/employee/bank-update-requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return await fetchGetApi(endpoint);
};

/**
 * Get bank update request by ID
 * @param {string} requestId - The ID of the request to retrieve
 * @returns {Promise<ApiResponse>} API response with request details
 */
export const getBankUpdateRequestById = async (requestId: string): Promise<ApiResponse> => {
  const endpoint = `api/admin/employee/bank-update-request/${requestId}`;
  return await fetchGetApi(endpoint);
};

/**
 * Approve a bank update request
 * @param {string} requestId - The ID of the request to approve
 * @param {string} approvedBy - MongoDB ObjectId of the admin approving the request
 * @returns {Promise<ApiResponse>} API response
 */
export const approveBankUpdateRequest = async (requestId: string, approvedBy: string): Promise<ApiResponse> => {
  const endpoint = `api/admin/employee/bank-update-request/approve/${requestId}`;
  const body = { approvedBy };
  return await fetchPutApi(endpoint, body);
};

/**
 * Reject a bank update request
 * @param {string} requestId - The ID of the request to reject
 * @param {string} rejectedBy - MongoDB ObjectId of the admin rejecting the request
 * @param {string} rejectionReason - Required rejection reason
 * @returns {Promise<ApiResponse>} API response
 */
export const rejectBankUpdateRequest = async (
  requestId: string, 
  rejectedBy: string, 
  rejectionReason: string
): Promise<ApiResponse> => {
  const endpoint = `api/admin/employee/bank-update-request/reject/${requestId}`;
  const body = { rejectedBy, rejectionReason };
  return await fetchPutApi(endpoint, body);
};

/**
 * Get pending bank update requests count
 * @returns {Promise<ApiResponse>} API response with count
 */
export const getPendingBankUpdateRequestsCount = async (): Promise<ApiResponse> => {
  const endpoint = 'api/admin/employee/bank-update-requests/pending/count';
  return await fetchGetApi(endpoint);
};

// Status badges configuration
export const STATUS_CONFIG: Record<string, { class: string; label: string }> = {
  pending: {
    class: 'badge badge-warning',
    label: 'Pending'
  },
  approved: {
    class: 'badge badge-success',
    label: 'Approved'
  },
  rejected: {
    class: 'badge badge-danger',
    label: 'Rejected'
  }
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get status badge configuration
 * @param {string} status - Request status
 * @returns {object} Badge configuration
 */
export const getStatusBadge = (status: string) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
};
